import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, FileText, Factory, AlertTriangle, ArrowUpDown } from "lucide-react";

const STATUS_STYLES = {
  "COVERED_BY_STOCK": "bg-emerald-100 text-emerald-800",
  "PLANNED": "bg-sky-100 text-sky-800",
  "PARTIALLY_PLANNED": "bg-amber-100 text-amber-800",
  "NEEDS_PRODUCTION": "bg-rose-100 text-rose-800",
  "REVIEW — STOCK UNKNOWN": "bg-stone-200 text-stone-700",
};

const COLUMNS = [
  ["productId", "Product ID"], ["sku", "SKU"], ["productName", "Product"], ["category", "Category"],
  ["unit", "UOM"], ["orderCount", "# Orders"], ["outstandingQty", "Outstanding"],
  ["currentFg", "FG Stock"], ["reservedFg", "Reserved"], ["availableFg", "Available"],
  ["netRequired", "Net Required"], ["plannedQty", "Planned"], ["balanceToPlan", "Balance"],
  ["earliestDelivery", "Earliest"], ["latestDelivery", "Latest"], ["requirementStatus", "Status"],
];

export default function OrderConsolidation() {
  const navigate = useNavigate();
  const { data: buyerData } = useResource("/buyers");
  const buyers = Array.isArray(buyerData) ? buyerData : [];

  const [statuses, setStatuses] = useState("CONFIRMED");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [outlet, setOutlet] = useState("");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState(null);
  const [view, setView] = useState("sku");   // sku | orders
  const [expanded, setExpanded] = useState({});
  const [sort, setSort] = useState({ key: "outstandingQty", dir: -1 });

  const qs = useMemo(() => {
    if (!applied) return null;
    const p = new URLSearchParams();
    Object.entries(applied).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  }, [applied]);
  const { data, isLoading, isError, error, refetch } = useResource(qs ? `/reports/order-consolidation?${qs}` : null);

  const applyFilters = () => setApplied({ statuses, dateFrom, dateTo, buyerId, poNumber, outlet, category, search });

  const rows = useMemo(() => {
    const rs = [...(data?.rows || [])];
    const { key, dir } = sort;
    rs.sort((a, b) => {
      const va = a[key], vb = b[key];
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va ?? "").localeCompare(String(vb ?? "")) * dir;
    });
    return rs;
  }, [data, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : (typeof rows[0]?.[key] === "number" ? -1 : 1) }));

  const doExport = async (fmt) => {
    if (!qs) { toast.error("Generate the report first"); return; }
    try {
      const res = await api.get(`/reports/order-consolidation/export?format=${fmt}&${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `order_consolidation.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt === "pdf" ? "PDF" : "Excel"} downloaded`);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
  };

  const sendToPlanning = (r) => {
    navigate("/production-plans", { state: {
      ocPrefill: [{ productCode: r.productId, plannedQty: r.balanceToPlan > 0 ? r.balanceToPlan : r.netRequired, productName: r.productName }],
      ocNote: `From Order Consolidation: ${r.productName} — net required ${fmtNumber(r.netRequired)} ${r.unit} (${r.orderCount} order(s), latest delivery ${r.latestDelivery || "—"})`,
      ocSourceOrders: [...new Set(r.lines.map((l) => l.orderNo))],
    }});
  };

  const num = (v) => fmtNumber(v ?? 0);

  return (
    <div className="space-y-5" data-testid="order-consolidation-page">
      <PageHeader title="Order Consolidation" subtitle="SKU-wise demand from eligible sales orders · saleable FG stock (reservations excluded) · valid production-plan coverage. Read-only — no orders, stock or plans are modified." />

      <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px]">Order Status</Label>
            <select data-testid="oc-status" value={statuses} onChange={(e) => setStatuses(e.target.value)} className="h-8 w-56 rounded-md border border-stone-200 bg-white px-2 text-xs">
              <option value="CONFIRMED">CONFIRMED (default)</option>
              <option value="CONFIRMED,PARTIALLY_DISPATCHED,DISPATCHED">CONFIRMED + partially dispatched</option>
              <option value="">All statuses</option>
              <option value="DRAFT">DRAFT</option><option value="CANCELLED">CANCELLED</option>
            </select>
          </div>
          <div><Label className="text-[10px]">Delivery from</Label><Input data-testid="oc-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-36 text-xs" /></div>
          <div><Label className="text-[10px]">Delivery to</Label><Input data-testid="oc-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-36 text-xs" /></div>
          <div>
            <Label className="text-[10px]">Buyer</Label>
            <select data-testid="oc-buyer" value={buyerId} onChange={(e) => setBuyerId(e.target.value)} className="h-8 w-48 rounded-md border border-stone-200 bg-white px-2 text-xs">
              <option value="">All buyers</option>
              {buyers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div><Label className="text-[10px]">PO #</Label><Input data-testid="oc-po" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="Buyer PO" className="h-8 w-32 text-xs" /></div>
          <div><Label className="text-[10px]">Outlet</Label><Input data-testid="oc-outlet" value={outlet} onChange={(e) => setOutlet(e.target.value)} placeholder="Store / outlet" className="h-8 w-32 text-xs" /></div>
          <div>
            <Label className="text-[10px]">Category</Label>
            <select data-testid="oc-category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-8 w-32 rounded-md border border-stone-200 bg-white px-2 text-xs">
              <option value="">All</option><option value="Bakery">Bakery</option><option value="Non-Bakery">Non-Bakery</option>
            </select>
          </div>
          <div><Label className="text-[10px]">Search product</Label><Input data-testid="oc-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ID / SKU / name" className="h-8 w-40 text-xs" /></div>
          <Button data-testid="oc-generate" size="sm" className="h-8 bg-amber-700 hover:bg-amber-800 text-xs" onClick={applyFilters}>Generate</Button>
          {data && (
            <>
              <Button data-testid="oc-export-excel" size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => doExport("excel")}><Download className="h-3.5 w-3.5" /> Excel (4 sheets)</Button>
              <Button data-testid="oc-export-pdf" size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => doExport("pdf")}><FileText className="h-3.5 w-3.5" /> PDF</Button>
            </>
          )}
        </div>
        <p className="mt-2 text-[11px] text-stone-500">Draft / cancelled / rejected orders are excluded by default. Outstanding = ordered − fulfilled per order line. Available stock never includes reservations; expired or non-active batches are excluded.</p>
      </div>

      {!applied ? null : isLoading ? <LoadingState label="Consolidating orders…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> : !data ? null : (
        <>
          {data.warnings?.length > 0 && (
            <div data-testid="oc-warnings" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{data.warnings.map((w, i) => <div key={i}>{w}</div>)}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
            {[["SKUs", data.totals.skus], ["Orders", data.totals.orders], ["Outstanding", num(data.totals.outstandingQty)],
              ["Available FG", num(data.totals.availableFg)], ["Net Required", num(data.totals.netRequired)],
              ["Planned", num(data.totals.plannedQty)], ["Balance To Plan", num(data.totals.balanceToPlan)]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-stone-200 bg-white px-3 py-2 shadow-sm" data-testid={`oc-kpi-${k.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <div className="text-base font-bold tabular-nums text-stone-900">{v}</div>
                <div className="text-[9px] font-semibold uppercase tracking-wide text-stone-500">{k}</div>
              </div>
            ))}
          </div>

          <div className="flex gap-1 rounded-lg border border-stone-200 bg-white p-1 shadow-sm w-fit">
            <button data-testid="oc-view-sku" onClick={() => setView("sku")} className={`rounded-md px-4 py-1.5 text-xs font-semibold ${view === "sku" ? "bg-amber-700 text-white" : "text-stone-600"}`}>SKU-wise Consolidation</button>
            <button data-testid="oc-view-orders" onClick={() => setView("orders")} className={`rounded-md px-4 py-1.5 text-xs font-semibold ${view === "orders" ? "bg-amber-700 text-white" : "text-stone-600"}`}>Order-wise View ({data.lines.length})</button>
          </div>

          {view === "sku" ? (
            rows.length === 0 ? <EmptyState title="No demand" message="No eligible order lines match the current filters." /> : (
              <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm">
                <table className="w-full text-xs" data-testid="oc-table">
                  <thead>
                    <tr className="border-b border-stone-200 bg-stone-50 text-left text-[10px] font-bold uppercase tracking-wide text-stone-500">
                      <th className="px-2 py-2" />
                      {COLUMNS.map(([k, label]) => (
                        <th key={k} className="cursor-pointer select-none px-2 py-2 hover:text-stone-800" onClick={() => toggleSort(k)} data-testid={`oc-sort-${k}`}>
                          <span className="inline-flex items-center gap-0.5">{label}<ArrowUpDown className="h-2.5 w-2.5" /></span>
                        </th>
                      ))}
                      <th className="px-2 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {rows.map((r) => (
                      <>
                        <tr key={r.productId} className="hover:bg-amber-50/40" data-testid={`oc-row-${r.productId}`}>
                          <td className="px-2 py-2">
                            <button data-testid={`oc-expand-${r.productId}`} onClick={() => setExpanded((e) => ({ ...e, [r.productId]: !e[r.productId] }))} className="text-stone-500 hover:text-amber-700">
                              {expanded[r.productId] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="px-2 py-2 font-mono">{r.productId}</td>
                          <td className="px-2 py-2 font-mono">{r.sku || "—"}</td>
                          <td className="px-2 py-2 font-medium text-stone-900">{r.productName}</td>
                          <td className="px-2 py-2">{r.category}</td>
                          <td className="px-2 py-2">{r.unit}</td>
                          <td className="px-2 py-2 text-right tabular-nums">{r.orderCount}</td>
                          <td className="px-2 py-2 text-right font-semibold tabular-nums">{num(r.outstandingQty)}</td>
                          <td className="px-2 py-2 text-right tabular-nums">{num(r.currentFg)}</td>
                          <td className="px-2 py-2 text-right tabular-nums text-stone-500">{num(r.reservedFg)}</td>
                          <td className="px-2 py-2 text-right tabular-nums">{num(r.availableFg)}</td>
                          <td className="px-2 py-2 text-right font-bold tabular-nums text-amber-800">{num(r.netRequired)}</td>
                          <td className="px-2 py-2 text-right tabular-nums" title={(r.coveringPlans || []).join(", ")}>{num(r.plannedQty)}</td>
                          <td className="px-2 py-2 text-right font-semibold tabular-nums">{num(r.balanceToPlan)}</td>
                          <td className="px-2 py-2">{r.earliestDelivery || "—"}</td>
                          <td className="px-2 py-2">{r.latestDelivery || "—"}</td>
                          <td className="px-2 py-2"><Badge className={`text-[9px] ${STATUS_STYLES[r.requirementStatus] || "bg-stone-100"}`}>{r.requirementStatus}</Badge>
                            {r.warnings?.length > 0 && <div className="mt-0.5 text-[9px] text-amber-700">{r.warnings[0]}</div>}</td>
                          <td className="px-2 py-2">
                            {r.balanceToPlan > 0 && (
                              <Button data-testid={`oc-send-plan-${r.productId}`} size="sm" variant="outline" className="h-7 gap-1 text-[10px] text-amber-800 border-amber-300 hover:bg-amber-50" onClick={() => sendToPlanning(r)}>
                                <Factory className="h-3 w-3" /> Send to Planning
                              </Button>
                            )}
                          </td>
                        </tr>
                        {expanded[r.productId] && (
                          <tr key={`${r.productId}-detail`} data-testid={`oc-detail-${r.productId}`}>
                            <td colSpan={18} className="bg-stone-50/70 px-6 py-3">
                              <table className="w-full text-[11px]">
                                <thead><tr className="text-left text-[9px] font-bold uppercase tracking-wide text-stone-500">
                                  {["Order #", "Buyer PO", "Buyer", "Outlet", "Delivery", "Ordered", "Fulfilled", "Outstanding", "Order Status"].map((h) => <th key={h} className="px-2 py-1">{h}</th>)}
                                </tr></thead>
                                <tbody className="divide-y divide-stone-100">
                                  {r.lines.map((l) => (
                                    <tr key={`${l.orderId}-${l.lineIndex}`} data-testid={`oc-line-${l.orderId}-${l.lineIndex}`}>
                                      <td className="px-2 py-1.5 font-mono">{l.orderNo}</td>
                                      <td className="px-2 py-1.5">{l.buyerPoNumber || "—"}</td>
                                      <td className="px-2 py-1.5">{l.buyerName}</td>
                                      <td className="px-2 py-1.5">{l.outlet || "—"}</td>
                                      <td className="px-2 py-1.5">{l.deliveryDate || "—"}</td>
                                      <td className="px-2 py-1.5 text-right tabular-nums">{num(l.orderedQty)}</td>
                                      <td className="px-2 py-1.5 text-right tabular-nums">{num(l.fulfilledQty)}</td>
                                      <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{num(l.outstandingQty)}</td>
                                      <td className="px-2 py-1.5">{l.orderStatus}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm">
              <table className="w-full text-xs" data-testid="oc-orders-table">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50 text-left text-[10px] font-bold uppercase tracking-wide text-stone-500">
                    {["Order #", "Buyer PO", "Buyer", "Outlet", "Delivery", "Product ID", "SKU", "Product", "Category", "Ordered", "Fulfilled", "Outstanding", "Status"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {data.lines.map((l) => (
                    <tr key={`ow-${l.orderId}-${l.lineIndex}`} className="hover:bg-amber-50/40" data-testid={`oc-ow-${l.orderId}-${l.lineIndex}`}>
                      <td className="px-2 py-2 font-mono">{l.orderNo}</td>
                      <td className="px-2 py-2">{l.buyerPoNumber || "—"}</td>
                      <td className="px-2 py-2">{l.buyerName}</td>
                      <td className="px-2 py-2">{l.outlet || "—"}</td>
                      <td className="px-2 py-2">{l.deliveryDate || "—"}</td>
                      <td className="px-2 py-2 font-mono">{l.productId}</td>
                      <td className="px-2 py-2 font-mono">{l.sku || "—"}</td>
                      <td className="px-2 py-2 font-medium">{l.productName}</td>
                      <td className="px-2 py-2">{l.category}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{num(l.orderedQty)}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{num(l.fulfilledQty)}</td>
                      <td className="px-2 py-2 text-right font-semibold tabular-nums">{num(l.outstandingQty)}</td>
                      <td className="px-2 py-2">{l.orderStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
